import React from 'react'
import { Box, Typography } from '@mui/material'

/**
 * The facts of a box, in columns that line up.
 *
 * Each fact used to be one cell reading "Label: value", so every value began wherever the label in
 * front of it happened to end and nothing lined up down the box. Here the label and the value are
 * separate cells, which is what makes a column of values a column.
 *
 * `minmax(0, 1fr)` on the value rather than `1fr`: a grid track will not shrink below its content
 * otherwise, so a long value pushed the columns out of the card instead of wrapping inside it.
 *
 * A fact marked `wide` takes a row to itself — an address does not belong in a quarter of a box.
 * Its label begins a new row at column 1, and its value runs from the next column to the last.
 * `2 / -1` rather than an end line alone: an end on its own leaves the start to be auto-placed,
 * which drops the value into the final column instead of spanning from its label.
 */
export default function FactGrid({ facts }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'max-content minmax(0, 1fr)',
          sm: 'max-content minmax(0, 1fr) max-content minmax(0, 1fr)',
        },
        alignItems: 'baseline',
        columnGap: 1,
        rowGap: 0.5,
      }}
    >
      {facts.map(({ key, label, value, wide }) => (
        <React.Fragment key={key}>
          <Typography
            variant="body2"
            component="div"
            sx={{ fontWeight: 'bold', ...(wide && { gridColumnStart: 1 }) }}
          >
            {label}:
          </Typography>
          <Typography
            variant="body2"
            component="div"
            sx={{ minWidth: 0, ...(wide && { gridColumn: '2 / -1' }) }}
          >
            {value}
          </Typography>
        </React.Fragment>
      ))}
    </Box>
  )
}
